import { C, cAdd, cMul, cConj, cScale, cAbs2 } from './complex';
import { X as PAULI_X, Z as PAULI_Z } from './gates';

/**
 * Shared n-qubit state-vector engine. Every visualization that needs a real
 * quantum state (Gates Playground, Circuit Builder, Q-Sphere, Multi-Qubit
 * Lab, QOTP security demo, ...) should build on these functions instead of
 * re-deriving its own math, so they all agree on the same underlying state.
 *
 * Basis-index convention: for n qubits, index i (0..2^n-1) is read as an
 * n-bit binary string MSB-first, e.g. n=2, i=1 -> "01" -> |01⟩. Qubit 0 is
 * the leftmost (most significant) bit — the top wire in a circuit diagram.
 */

export const dim = (n) => 1 << n;

// The bit value (0 or 1) of `qubit` within an n-qubit basis index `i`.
export const bitOf = (i, qubit, n) => (i >> (n - 1 - qubit)) & 1;

export const bitstring = (i, n) => i.toString(2).padStart(n, '0');

// |000...0⟩
export const basisState = (n, index = 0) => {
  const state = new Array(dim(n)).fill(null).map(() => C(0, 0));
  state[index] = C(1, 0);
  return state;
};

export const zeroState = (n) => basisState(n, 0);

export const norm = (state) => Math.sqrt(state.reduce((sum, a) => sum + cAbs2(a), 0));

export const normalize = (state) => {
  const n = norm(state);
  if (n === 0) throw new Error('Cannot normalize a zero state vector');
  return state.map((a) => cScale(a, 1 / n));
};

export const probabilities = (state) => state.map(cAbs2);

/**
 * Apply a single-qubit gate matrix [[a,b],[c,d]] to `qubit` within an
 * n-qubit state, leaving every other qubit untouched.
 */
export const applyGate = (state, matrix, qubit, n) => {
  const newState = state.slice();
  const d = dim(n);
  const stride = 1 << (n - 1 - qubit);
  for (let i = 0; i < d; i++) {
    if (bitOf(i, qubit, n) !== 0) continue;
    const i1 = i | stride;
    const a0 = state[i];
    const a1 = state[i1];
    newState[i] = cAdd(cMul(matrix[0][0], a0), cMul(matrix[0][1], a1));
    newState[i1] = cAdd(cMul(matrix[1][0], a0), cMul(matrix[1][1], a1));
  }
  return newState;
};

/**
 * Apply a single-qubit gate matrix to `target`, only where `control` is |1⟩
 * (e.g. CNOT = applyControlledGate(state, X, control, target, n)).
 */
export const applyControlledGate = (state, matrix, control, target, n) => {
  const newState = state.slice();
  const d = dim(n);
  const stride = 1 << (n - 1 - target);
  for (let i = 0; i < d; i++) {
    if (bitOf(i, target, n) !== 0) continue;
    if (bitOf(i, control, n) !== 1) continue;
    const i1 = i | stride;
    const a0 = state[i];
    const a1 = state[i1];
    newState[i] = cAdd(cMul(matrix[0][0], a0), cMul(matrix[0][1], a1));
    newState[i1] = cAdd(cMul(matrix[1][0], a0), cMul(matrix[1][1], a1));
  }
  return newState;
};

// SWAP q1 and q2 (their own inverse, so a single pass suffices).
export const applySwap = (state, n, q1, q2) => {
  const d = dim(n);
  const newState = new Array(d);
  for (let i = 0; i < d; i++) {
    const b1 = bitOf(i, q1, n);
    const b2 = bitOf(i, q2, n);
    if (b1 === b2) {
      newState[i] = state[i];
    } else {
      const stride1 = 1 << (n - 1 - q1);
      const stride2 = 1 << (n - 1 - q2);
      // Flip both bits to get the index whose amplitude belongs at i.
      const source = i ^ stride1 ^ stride2;
      newState[i] = state[source];
    }
  }
  return newState;
};

// Combine two independent subsystems into one joint state (Kronecker
// product); A's qubits become the more-significant (left-hand) qubits.
export const tensorProduct = (stateA, stateB) => {
  const result = [];
  for (const a of stateA) {
    for (const b of stateB) {
      result.push(cMul(a, b));
    }
  }
  return result;
};

// ρ = |ψ⟩⟨ψ| as a dim×dim matrix of complex entries.
export const densityMatrixFromState = (state) => {
  const d = state.length;
  const rho = [];
  for (let i = 0; i < d; i++) {
    const row = [];
    for (let j = 0; j < d; j++) {
      row.push(cMul(state[i], cConj(state[j])));
    }
    rho.push(row);
  }
  return rho;
};

/**
 * Weighted average of several density matrices: sum_i prob_i * rho_i. This
 * is the real math behind "what does an eavesdropper see, averaged over an
 * unknown random key" — e.g. the QOTP security demo averages the four
 * equally-likely X^a Z^b encryptions of a state to show the result is I/2
 * regardless of the original state.
 */
export const mixDensityMatrices = (weighted) => {
  const d = weighted[0].rho.length;
  const mixed = Array.from({ length: d }, () => Array.from({ length: d }, () => C(0, 0)));
  for (const { prob, rho } of weighted) {
    for (let i = 0; i < d; i++) {
      for (let j = 0; j < d; j++) {
        mixed[i][j] = cAdd(mixed[i][j], cScale(rho[i][j], prob));
      }
    }
  }
  return mixed;
};

// Remove `qubit`'s bit from an n-bit index, packing the remaining bits.
const stripBit = (index, qubit, n) => {
  let result = 0;
  for (let q = 0; q < n; q++) {
    if (q === qubit) continue;
    result = (result << 1) | bitOf(index, q, n);
  }
  return result;
};

/**
 * Trace out every qubit except `keepQubit`, returning that qubit's
 * (possibly mixed) 2x2 reduced density matrix. This is the real math
 * behind "what does a single qubit inside an entangled system look like on
 * its own" — e.g. tracing out either half of a Bell pair gives I/2, which
 * is exactly the "no local information" result the QOTP security demo
 * relies on.
 */
export const partialTraceKeepQubit = (rho, n, keepQubit) => {
  const d = rho.length;
  const reduced = [[C(0), C(0)], [C(0), C(0)]];
  for (let i = 0; i < d; i++) {
    const a = bitOf(i, keepQubit, n);
    const otherI = stripBit(i, keepQubit, n);
    for (let j = 0; j < d; j++) {
      const b = bitOf(j, keepQubit, n);
      const otherJ = stripBit(j, keepQubit, n);
      if (otherI !== otherJ) continue;
      reduced[a][b] = cAdd(reduced[a][b], rho[i][j]);
    }
  }
  return reduced;
};

/**
 * Bloch vector {x, y, z} of a single-qubit density matrix, via
 * x = Tr(ρX) = 2·Re(ρ01), y = Tr(ρY) = -2·Im(ρ01), z = Tr(ρZ) = ρ00 - ρ11.
 */
export const blochVector = (rho2x2) => ({
  x: 2 * rho2x2[0][1].re,
  y: -2 * rho2x2[0][1].im,
  z: rho2x2[0][0].re - rho2x2[1][1].re,
});

export const blochVectorForQubit = (state, n, qubit) => {
  if (n === 1) return blochVector(densityMatrixFromState(state));
  const rho = densityMatrixFromState(state);
  return blochVector(partialTraceKeepQubit(rho, n, qubit));
};

/**
 * Measure a single qubit via the Born rule, collapsing the state to be
 * consistent with the outcome and renormalizing. `rng` defaults to
 * Math.random but accepts an injected function for deterministic tests.
 */
export const measureQubit = (state, n, qubit, rng = Math.random) => {
  const d = dim(n);
  let p0 = 0;
  for (let i = 0; i < d; i++) {
    if (bitOf(i, qubit, n) === 0) p0 += cAbs2(state[i]);
  }
  const p1 = 1 - p0;
  const result = rng() < p0 ? 0 : 1;
  const pOutcome = result === 0 ? p0 : p1;
  const scale = 1 / Math.sqrt(pOutcome);
  const collapsed = state.map((amp, i) =>
    bitOf(i, qubit, n) === result ? cScale(amp, scale) : C(0, 0)
  );
  return { result, state: collapsed, p0, p1 };
};

// Measure every qubit at once (a full computational-basis measurement).
export const measureAll = (state, n, rng = Math.random) => {
  const probs = probabilities(state);
  const r = rng();
  let cumulative = 0;
  let index = probs.length - 1;
  for (let i = 0; i < probs.length; i++) {
    cumulative += probs[i];
    if (r < cumulative) {
      index = i;
      break;
    }
  }
  return { index, bitstring: bitstring(index, n), state: basisState(n, index) };
};

/**
 * Quantum One-Time Pad: encrypts `qubit` with a random 2-bit key (a,b) by
 * applying X^a Z^b — the standard 00->I, 01->Z, 10->X, 11->XZ mapping.
 * qotpDecrypt reverses it: since X and Z don't commute, undoing requires
 * applying them in the OPPOSITE order, not just repeating the same gates.
 */
export const qotpEncrypt = (state, a, b, n = 1, qubit = 0) => {
  let s = b ? applyGate(state, PAULI_Z, qubit, n) : state;
  s = a ? applyGate(s, PAULI_X, qubit, n) : s;
  return s;
};

export const qotpDecrypt = (state, a, b, n = 1, qubit = 0) => {
  let s = a ? applyGate(state, PAULI_X, qubit, n) : state;
  s = b ? applyGate(s, PAULI_Z, qubit, n) : s;
  return s;
};
